/**
 * Sidebar Component - Manus AI Style
 *
 * Left navigation with three sections (CORE / GROWTH / SYSTEM) — flat
 * items, no accordion. Desktop collapses to an icon-only rail; mobile
 * uses an overlay drawer. Collapse is toggled from the header control
 * (and from TopBar / MobileAppBar via the same AppLayout handler).
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Briefcase,
  CalendarDays,
  Inbox,
  MessageCircle,
  PanelLeft,
  PanelLeftClose,
  User,
  Workflow,
  X,
} from 'lucide-react';
import { BrandLogo } from '@/components/brand/BrandLogo';
import { cn } from '@/lib/utils';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useChatStore } from '../../stores/chatStore';
import { useInboxStore } from '../../stores/inboxStore';
import { SidebarUserFooter } from './SidebarUserFooter';
import { prefetchRoute } from '../../app/routePrefetch';

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  badge?: string | number;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

interface SidebarProps {
  isOpen: boolean;
  collapsed?: boolean;
  onClose: () => void;
  /** Desktop-only: collapses the rail to icons / expands it again. */
  onToggleCollapse?: () => void;
}

export function Sidebar({
  isOpen,
  collapsed = false,
  onClose,
  onToggleCollapse,
}: SidebarProps) {
  const location = useLocation();
  const isDesktop = !useIsMobile();
  /** Icon rail only on desktop — the mobile drawer always shows labels. */
  const railCollapsed = collapsed && isDesktop;
  const chatUnread = useChatStore((s) => s.totalUnread);
  const inboxUnread = useInboxStore((s) => s.unread);

  const sections: NavSection[] = [
    {
      title: 'CORE',
      items: [
        {
          label: 'Business Hub',
          path: '/business',
          icon: <Briefcase className="h-5 w-5" />,
        },
        {
          label: 'Automations',
          path: '/studio?tab=my-flows',
          icon: <Workflow className="h-5 w-5" />,
        },
        {
          label: 'Workspace',
          path: '/chat',
          icon: <MessageCircle className="h-5 w-5" />,
          badge: chatUnread > 0 ? (chatUnread > 99 ? '99+' : chatUnread) : undefined,
        },
      ],
    },
    {
      title: 'GROWTH',
      items: [
        {
          label: 'Calendar',
          path: '/calendar',
          icon: <CalendarDays className="h-5 w-5" />,
        },
        {
          label: 'Inbox',
          path: '/inbox',
          icon: <Inbox className="h-5 w-5" />,
          badge: inboxUnread > 0 ? (inboxUnread > 99 ? '99+' : inboxUnread) : undefined,
        },
      ],
    },
    {
      title: 'SYSTEM',
      items: [
        {
          label: 'Account',
          path: '/account',
          icon: <User className="h-5 w-5" />,
        },
      ],
    },
  ];

  const isActive = (target: string) => {
    const path = target.split('?')[0];
    if (path === '/business' && location.pathname === '/') return true;

    if (path === '/studio' && (
      location.pathname.startsWith('/studio') ||
      location.pathname.startsWith('/automations') ||
      location.pathname.startsWith('/marketing/email') ||
      location.pathname.startsWith('/agent') ||
      location.pathname.startsWith('/popups')
    )) {
      return true;
    }

    if (path === '/calendar' && location.pathname.startsWith('/marketing/social')) {
      return true;
    }

    if (path === '/account' && (
      location.pathname.startsWith('/account') ||
      location.pathname.startsWith('/brand-dna') ||
      location.pathname.startsWith('/settings/integrations')
    )) {
      return true;
    }

    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const handleNavClick = () => {
    if (window.innerWidth < 1024) {
      onClose();
    }
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <aside
        className={cn(
          'fixed top-0 left-0 z-50 flex h-screen flex-col',
          'border-r border-gray-200 bg-white',
          'transition-[width,transform] duration-300 ease-in-out',
          railCollapsed ? 'lg:w-16' : 'lg:w-72',
          'w-72',
          'lg:translate-x-0',
          isOpen ? 'translate-x-0' : '-translate-x-full',
        )}
        data-collapsed={railCollapsed ? 'true' : 'false'}
      >
        {/* Header — logo + collapse (desktop) / close (mobile) */}
        <div
          className={cn(
            'flex h-16 shrink-0 items-center border-b border-gray-200',
            railCollapsed ? 'justify-center px-1.5' : 'justify-between gap-2 px-3',
          )}
        >
          <Link
            to="/"
            className={cn(
              'flex min-w-0 items-center overflow-hidden rounded-md',
              railCollapsed ? 'justify-center' : 'flex-1',
            )}
            title="BuzzBit X"
          >
            <BrandLogo
              size={railCollapsed ? 'sm' : 'xs'}
              variant={railCollapsed ? 'mark' : 'lockup'}
            />
          </Link>

          {!railCollapsed && (
            <div className="flex shrink-0 items-center gap-0.5">
              {isDesktop && onToggleCollapse ? (
                <button
                  type="button"
                  onClick={onToggleCollapse}
                  className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300"
                  aria-label="Collapse sidebar"
                  aria-expanded
                >
                  <PanelLeftClose className="h-4 w-4" />
                </button>
              ) : null}

              {!isDesktop && (
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300"
                  aria-label="Close sidebar"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto overflow-x-hidden py-3" aria-label="Primary">
          {railCollapsed && onToggleCollapse ? (
            <div className="mb-2 px-1.5">
              <button
                type="button"
                onClick={onToggleCollapse}
                className="flex w-full items-center justify-center rounded-lg px-2 py-2.5 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300"
                aria-label="Expand sidebar"
                aria-expanded={false}
              >
                <PanelLeft className="h-5 w-5" />
              </button>
            </div>
          ) : null}

          {sections.map((section, sectionIndex) => (
            <div key={section.title}>
              {sectionIndex > 0 && (
                <div
                  className={cn(
                    'my-3 border-t border-gray-100',
                    railCollapsed ? 'mx-2' : 'mx-3',
                  )}
                />
              )}

              {!railCollapsed && (
                <div className="mb-1.5 px-4">
                  <h2 className="text-[11px] font-medium uppercase tracking-[0.04em] text-gray-400">
                    {section.title}
                  </h2>
                </div>
              )}

              <div className={cn('space-y-0.5', railCollapsed ? 'px-1.5' : 'px-2')}>
                {section.items.map((item) => {
                  const active = isActive(item.path);

                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={handleNavClick}
                      onMouseEnter={() => prefetchRoute(item.path)}
                      onFocus={() => prefetchRoute(item.path)}
                      aria-current={active ? 'page' : undefined}
                      title={railCollapsed ? item.label : undefined}
                      aria-label={item.label}
                      className={cn(
                        'flex items-center rounded-lg text-sm font-medium transition-colors duration-150',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300',
                        railCollapsed ? 'justify-center px-2 py-2.5' : 'gap-3 px-3 py-2',
                        active
                          ? 'bg-gray-100 text-gray-900'
                          : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900',
                      )}
                    >
                      <span className="relative shrink-0">
                        {item.icon}
                        {railCollapsed && item.badge ? (
                          <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-gray-900" />
                        ) : null}
                      </span>
                      {!railCollapsed && (
                        <>
                          <span className="flex-1 truncate">{item.label}</span>
                          {item.badge ? (
                            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold tabular-nums text-gray-700">
                              {item.badge}
                            </span>
                          ) : null}
                        </>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <SidebarUserFooter collapsed={railCollapsed} />
      </aside>
    </>
  );
}

export default Sidebar;
